import React, { createContext, useContext, useEffect, useState, useMemo } from "react";

const CartCtx = createContext(null);
const STORAGE_KEY = "msc_cart_v1";

function readStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

/**
 * Cart item shape: { id, slug, name, price_cents, featured_image, qty, max_qty }
 */
export function CartProvider({ children }) {
  const [items, setItems] = useState(() => readStorage());
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Persist on change
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch { /* ignore */ }
  }, [items]);

  const add = (product, qty = 1, variant = null) => {
    if (!product?.id) return;
    setItems(prev => {
      // Clé composite pour différencier les variantes d'un même produit
      const lineKey = variant ? `${product.id}::${variant.id}` : product.id;
      const idx = prev.findIndex(p => (p.line_key || p.id) === lineKey);
      const maxQty = variant
        ? (product.stock_tracking !== false ? (variant.stock_qty || 0) : 99)
        : (product.stock_tracking ? product.stock_qty : 99);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: Math.min(maxQty, next[idx].qty + qty) };
        return next;
      }
      const variantLabel = variant ? Object.values(variant.options || {}).join(" · ") : "";
      return [...prev, {
        id: product.id,
        line_key: lineKey,
        slug: product.slug,
        name: variant ? `${product.name} — ${variantLabel}` : product.name,
        price_cents: variant ? (variant.price_cents || product.price_cents) : product.price_cents,
        featured_image: (variant && variant.image) || product.featured_image || "",
        qty: Math.min(maxQty, qty),
        max_qty: maxQty,
        stock_tracking: product.stock_tracking !== false,
        weight_g: (variant?.weight_g || product.weight_g || 0),
        variant_id: variant?.id || null,
        variant_options: variant?.options || null,
        variant_sku: variant?.sku || product.sku || null,
      }];
    });
    setDrawerOpen(true);
  };

  const remove = (lineKey) => setItems(prev => prev.filter(p => (p.line_key || p.id) !== lineKey));
  const setQty = (lineKey, qty) => setItems(prev =>
    prev.map(p => (p.line_key || p.id) === lineKey
      ? { ...p, qty: Math.max(1, Math.min(p.max_qty ?? 99, qty)) }
      : p
    )
  );
  const clear = () => setItems([]);

  const { count, subtotal_cents } = useMemo(() => ({
    count: items.reduce((s, p) => s + (p.qty || 0), 0),
    subtotal_cents: items.reduce((s, p) => s + p.price_cents * (p.qty || 0), 0),
  }), [items]);

  return (
    <CartCtx.Provider value={{ items, count, subtotal_cents, add, remove, setQty, clear, drawerOpen, setDrawerOpen }}>
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => {
  const ctx = useContext(CartCtx);
  if (!ctx) throw new Error("useCart must be used within <CartProvider>");
  return ctx;
};
